import React from 'react';

import { readCMSValue, getDefault } from 'modules/shared-cms/utils';
import {
    ZoomedRibbonBloc,
    DualStaggeredBloc,
    VideoBloc,
    TitleBlocContainer,
    DoubleStaggeredBlocContainer,
    LogoContainer,
    ZoomedPortfolioBloc,
    MentionContainer,
    LogosBloc,
} from 'modules/shared-cms-advanced/components';
import { HtmlContainer } from 'modules/shared-ui/components';

import { DualStaggeredBlocProps } from 'modules/shared-cms-advanced/components/base/dual-staggered-bloc/dual-staggered-bloc-type';
import { WeddingEditorialLandingProps } from './wedding-editorial-landing-type';

import enhancer from './wedding-editorial-landing-enhancer';

import styles from './wedding-editorial-landing-page.module.css';

const weddingEditorialLandingPage: React.FC<WeddingEditorialLandingProps> = React.memo(
    ({
        editoText,
        editoText2,
        videoLink: wrVideoLink,
        logoPicture,
        logos: wrLogos,
        mentionLabel,
        zoomedRibbonTitle,
        zoomedRibbonTextColor,
        zoomedRibbonColor,
        zoomedRibbonItems: zoomedRibbon1,
        zoomedRibbon2Title,
        zoomedRibbon2TextColor,
        zoomedRibbon2Color,
        zoomedRibbon2Items: zoomedRibbon2,
        zoomedRibbon3Title,
        zoomedRibbon3TextColor,
        zoomedRibbon3Color,
        zoomedRibbon3Items: zoomedRibbon3,
        collectionTitle,
        collectionTextColor,
        collectionColor,
        collection,
        collection2Title,
        collection2TextColor,
        collection2Color,
        collection2,
        collection3Title,
        collection3TextColor,
        collection3Color,
        collection3,
        ...rest
    }: WeddingEditorialLandingProps) => {
        const {
            wrEditoText = getDefault(readCMSValue(editoText), ''),
            wrEditoText2 = getDefault(readCMSValue(editoText2), ''),
            zoomedRibbon1Items = getDefault(readCMSValue(zoomedRibbon1), []),
            zoomedRibbon2Items = getDefault(readCMSValue(zoomedRibbon2), []),
            zoomedRibbon3Items = getDefault(readCMSValue(zoomedRibbon3), []),
            collection1Items = getDefault(readCMSValue(collection), []),
            collection2Items = getDefault(readCMSValue(collection2), []),
            collection3Items = getDefault(readCMSValue(collection3), []),
            videoLink = getDefault(readCMSValue(wrVideoLink), ''),
            logos = getDefault(readCMSValue(wrLogos), []),
        } = {};

        return (
            <React.Fragment>
                <div className={`${styles.root} full-height full-width`}>
                    <TitleBlocContainer {...rest} />

                    {wrEditoText !== '' && (
                        <div className={`${styles.blockContainer} ${styles.editoText}`}>
                            <HtmlContainer htmlContent={wrEditoText} />
                        </div>
                    )}

                    {/* Image quinconce */}
                    <DualStaggeredBloc {...(rest as DualStaggeredBlocProps)} />

                    {/* Blocs Selection */}
                    {zoomedRibbon1Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.zoomedRibbonBloc}`}>
                            <ZoomedRibbonBloc
                                {...rest}
                                nbElement={6}
                                title={zoomedRibbonTitle}
                                textColor={zoomedRibbonTextColor}
                                color={zoomedRibbonColor}
                                list={zoomedRibbon1Items}
                            />
                        </div>
                    )}

                    {zoomedRibbon2Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.zoomedRibbonBloc}`}>
                            <ZoomedRibbonBloc
                                {...rest}
                                nbElement={6}
                                title={zoomedRibbon2Title}
                                textColor={zoomedRibbon2TextColor}
                                color={zoomedRibbon2Color}
                                list={readCMSValue(zoomedRibbon2Items)}
                            />
                        </div>
                    )}

                    {zoomedRibbon3Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.zoomedRibbonBloc}`}>
                            <ZoomedRibbonBloc
                                {...rest}
                                nbElement={6}
                                title={zoomedRibbon3Title}
                                textColor={zoomedRibbon3TextColor}
                                color={zoomedRibbon3Color}
                                list={readCMSValue(zoomedRibbon3Items)}
                            />
                        </div>
                    )}

                    {/* Blocs Collection */}
                    {collection1Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.collectionBloc}`}>
                            <ZoomedPortfolioBloc
                                {...rest}
                                list={collection1Items}
                                nbElement={6}
                                itemStyle={{
                                    padding: '2rem',
                                }}
                                title={collectionTitle}
                                color={collectionColor}
                                textColor={collectionTextColor}
                                collection
                            />
                        </div>
                    )}

                    {collection2Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.collectionBloc}`}>
                            <ZoomedPortfolioBloc
                                {...rest}
                                list={collection2Items}
                                nbElement={6}
                                itemStyle={{
                                    padding: '2rem',
                                }}
                                title={collection2Title}
                                color={collection2Color}
                                textColor={collection2TextColor}
                                collection
                            />
                        </div>
                    )}

                    {collection3Items.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.collectionBloc}`}>
                            <ZoomedPortfolioBloc
                                {...rest}
                                list={collection3Items}
                                nbElement={6}
                                itemStyle={{
                                    padding: '2rem',
                                }}
                                title={collection3Title}
                                color={collection3Color}
                                textColor={collection3TextColor}
                                collection
                            />
                        </div>
                    )}

                    {/* Video */}
                    {videoLink !== '' && (
                        <div className={`${styles.blockContainer} ${styles.videoBloc}`}>
                            <VideoBloc videoLink={videoLink} {...rest} />
                        </div>
                    )}

                    {wrEditoText2 !== '' && (
                        <div className={`${styles.blockContainer} ${styles.editoText2}`}>
                            <HtmlContainer htmlContent={wrEditoText2} />
                        </div>
                    )}

                    {logos.length > 0 && (
                        <div className={`${styles.blockContainer} ${styles.logos}`}>
                            <LogosBloc logos={logos} />
                        </div>
                    )}

                    {/* Avantage */}
                    <div className={styles.blockContainer}>
                        <DoubleStaggeredBlocContainer {...rest} />
                    </div>

                    {logoPicture && (
                        <div className={`${styles.blockContainer} ${styles.logo}`}>
                            <LogoContainer logo={logoPicture} {...rest} />
                        </div>
                    )}

                    {mentionLabel && (
                        <div className={`${styles.blockContainer} ${styles.mention}`}>
                            <MentionContainer mention={mentionLabel} mentionFontSize={10.5} mentionTextColor="black" />
                        </div>
                    )}
                </div>
            </React.Fragment>
        );
    },
);

export const WeddingEditorialLandingPage = enhancer(weddingEditorialLandingPage);
